import { selectValidatedExternalInvitations } from '@/domain/external-invitation/external-invitations.selectors';
import { getImpactCo2AnswersForCategory } from '@/domain/impact-co2/actions/get-impact-co2-answers-for-category.action';
import { ImpactCo2Answer } from '@/domain/impact-co2/entities/question.entity';
import { selectAllImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { selectProjectFestivalCalculateOptions } from '@/domain/project-festival/project-festival.selectors';
import { SnackbarGateway } from '@/domain/snackbar/gateways/snackbar.gateway';
import { AppDispatch, RootState } from '@/domain/store';
import { translate } from '@/ui/i18n';

import { ExportReportExcelUtils } from './utils/export-report-excel.utils';

export async function exportExcel(
  filename: string,
  projectId: number,
  snackbarGateway: SnackbarGateway,
  getState: () => RootState,
  dispatch: AppDispatch,
) {
  try {
    // fetch public answers before export
    await fetchPublicAnswersBeforeExcelExport({ projectId, dispatch });

    // get answers
    const answers = getAnswersToExportExcel(getState());

    // run export
    ExportReportExcelUtils.export(filename, answers);

    showSnackbarSuccess(snackbarGateway);
  } catch (error) {
    showSnackbarError(snackbarGateway);
    throw error;
  }
}

async function fetchPublicAnswersBeforeExcelExport({
  projectId,
  dispatch,
}: {
  projectId: number;
  dispatch: AppDispatch;
}) {
  await dispatch(
    getImpactCo2AnswersForCategory({
      projectId,
      category: 'MOBILITY_PUBLIC',
    }),
  );
  await dispatch(
    getImpactCo2AnswersForCategory({
      projectId,
      category: 'ACCOMMODATION_PUBLIC',
    }),
  );
}

/**
 * Filter answers before run export
 */
function getAnswersToExportExcel(state: RootState): ImpactCo2Answer[] {
  // get all answers
  const answers = selectAllImpactCo2Answers(state);

  // found validated userId (code) from externalInvitations
  const validatedExternalInvitations =
    selectValidatedExternalInvitations(state);
  const validatedExternalUserIds = validatedExternalInvitations.map(
    (x) => x.code,
  );

  // get includePublicAnswersForCo2 to define if we keep public answers
  let includePublicAnswersForCo2 = false;
  const calculateOptions = selectProjectFestivalCalculateOptions(state);
  if (calculateOptions) {
    includePublicAnswersForCo2 = calculateOptions.includePublicAnswersForCo2;
  }

  // filter answers
  // remove exnternal answers not validated
  // keep *_PUBLIC answers if the user included it it webapp
  const filteredAnswers = answers.filter(
    (x) =>
      x.isExternal === false ||
      (includePublicAnswersForCo2 && x.category === 'MOBILITY_PUBLIC') ||
      (includePublicAnswersForCo2 && x.category === 'ACCOMMODATION_PUBLIC') ||
      (x.isExternal === true && validatedExternalUserIds.includes(x.userId)),
  );

  return filteredAnswers;
}

function showSnackbarSuccess(snackbarGateway: SnackbarGateway) {
  snackbarGateway.success(translate('ReportExporter.Message.success'));
}

function showSnackbarError(snackbarGateway: SnackbarGateway) {
  snackbarGateway.error(translate('ReportExporter.Message.error-nomessage'));
}
